import { Button } from "@/components/ui/button";
import { useFormStore } from "@/contexts/paymentFlowContext";
import useCurrencyStore from "@/store/currencyStore";
import { formatNumber, getConversionApproximation } from "@/utils/currency";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface StepFooterProps {
  readonly nextStep: () => void;
  donationtype?: string;
}

const StepFooter = ({ nextStep, donationtype }: StepFooterProps) => {
  const { t } = useTranslation();
  const { hasCompletedAllSteps, formError, isSpecialFormInputAmountInUSD, isSpecial } =
    useFormStore();
  const { currency, conversionRate } = useCurrencyStore();
  const { inputAmount, activeTab } = useFormStore();

  const conversionApproximation = useMemo(
    () =>
      getConversionApproximation(inputAmount, isSpecialFormInputAmountInUSD ? "fromUSD" : "toUSD"),
    [currency, conversionRate, inputAmount, isSpecialFormInputAmountInUSD],
  );
  const displayAmount = useMemo(() => {
    if (!inputAmount) {
      return { primary: "", secondary: "" };
    }
    const defaultCurrencies = ["USD", "EGP"];
    const isThirdCurrency = !defaultCurrencies.includes(currency);

    if (isSpecialFormInputAmountInUSD && isSpecial) {
      // Special Form Cases
      if (currency === "USD") {
        // USD special form: show USD amount only
        return {
          primary: `$${formatNumber(inputAmount)}`,
          secondary: "",
        };
      } else if (conversionApproximation) {
        // AED special form: show AED amount with USD in parentheses
        return {
          primary: `~${currency} ${conversionApproximation}`,
          secondary: `($${formatNumber(inputAmount)})`,
        };
      }
    } else {
      // Normal Form Cases
      if (isThirdCurrency && conversionApproximation) {
        // Third currency normal form: show third currency amount with USD in parentheses
        return {
          primary: `~${currency} ${formatNumber(inputAmount)}`,
          secondary: `($${conversionApproximation})`,
        };
      }
    }

    // Default case: existing logic
    return {
      primary: conversionApproximation
        ? `~${currency} ${conversionApproximation}`
        : `${currency} ${formatNumber(inputAmount)}`,
      secondary: conversionApproximation ? `($${formatNumber(inputAmount)})` : "",
    };
  }, [currency, conversionApproximation, inputAmount, isSpecialFormInputAmountInUSD, isSpecial]);

  return (
    <div className="flex w-full flex-col gap-1 border-t border-[#CCC2BF] bg-background py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0 md:py-6">
      {formError && (
        <p className="mt-1 block text-sm text-[#F04438] sm:hidden">
          {t("donationFlow.globalError")}
        </p>
      )}
      {inputAmount ? (
        <div className="flex w-full flex-col gap-1 sm:items-start">
          <div className="hidden w-full items-center justify-between sm:flex sm:justify-start sm:gap-1">
            <p className="hidden md:block">{t("StepsContainer.donate")}</p>
            <p className="font-bold">{displayAmount.primary}</p>
            {displayAmount.secondary && <span className="text-sm">{displayAmount.secondary}</span>}
          </div>

          <div className="flex w-full justify-between sm:hidden">
            <p className="text-xs text-[#AC9CA0]">
              {activeTab === "once" ? t("StepsContainer.once") : t("donationFlow.monthly")}{" "}
              {t("StepsContainer.donation")} {donationtype}
            </p>
            <div className="flex items-center gap-1">
              <p className="text-sm font-bold">{displayAmount.primary}</p>
              {displayAmount.secondary && (
                <span className="text-sm">{displayAmount.secondary}</span>
              )}{" "}
            </div>
          </div>

          <p className="hidden text-sm text-[#AC9CA0] sm:block">
            {activeTab === "once" ? t("StepsContainer.once") : t("donationFlow.monthly")}{" "}
            {t("StepsContainer.donation")} {donationtype}
          </p>
        </div>
      ) : (
        <div></div>
      )}
      <div className="hidden flex-grow sm:block"></div>
      <Button
        onClick={nextStep}
        className="h-full w-full bg-primary font-bold text-foreground hover:bg-foreground hover:text-light sm:w-[14rem] md:px-10 md:py-4"
      >
        {hasCompletedAllSteps ? t("donationFlow.donateNow") : t("donationFlow.next")}
      </Button>
    </div>
  );
};

export default StepFooter;
